import {useState} from "react";
import {PaylordPoolSettings} from "./PaylordPoolSettings";
import type {MerchantResponse} from "../../../api/admin/merchants";
import {linkMerchantExternalPair, setMerchantExternalOrders} from "../../../api/admin/merchants";

/**
 * Внешний блок карточки мерчанта: кабинет площадки, переключатель «принимать
 * заявки» и признак гарантии расчёта.
 *
 * У обычного мерчанта, пришедшего по API, блок не отображается вовсе — вместо
 * него показывается только действие «связать с кабинетом».
 *
 * Мерчанта площадки здесь нет и быть не должно: баланс USDT и расчёт на
 * площадке принадлежат кабинету, поэтому кабинет — это и есть наш мерчант,
 * сколько бы клиентов площадки через него ни прошло. Чей это клиент — свойство
 * заявки, и живёт оно на заявке.
 */
export function ExternalMerchantSection({
    merchant,
    token,
    onChanged,
}: {
    merchant: MerchantResponse;
    token: string | null;
    onChanged: (updated: MerchantResponse) => void;
}) {
    const link = merchant.external;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [source, setSource] = useState("paylord");
    const [accountCode, setAccountCode] = useState("");

    async function run(action: () => Promise<MerchantResponse>) {
        if (!token) return;
        setBusy(true);
        setError(null);
        try {
            onChanged(await action());
            setFormOpen(false);
        } catch (cause) {
            // Отказ обязан назвать причину: занятый кабинет и мерчанта, который
            // его держит. Сообщение приходит с бэкенда — здесь не переписывается.
            setError(cause instanceof Error ? cause.message : String(cause));
        } finally {
            setBusy(false);
        }
    }

    if (!link) {
        return (
            <section className="merchant-rate-block merchant-external-block">
                <div className="section-header">
                    <div>
                        <h3>Кабинет площадки</h3>
                        <p className="section-hint">
                            Мерчант пришёл к нам напрямую по API. Если он вдобавок работает через
                            площадку, кабинет указывают здесь один раз: общего идентификатора у нас
                            с площадкой нет, автомат его не узнает.
                        </p>
                    </div>
                    {!formOpen && (
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={() => {
                                setError(null);
                                setFormOpen(true);
                            }}
                        >
                            Связать с кабинетом
                        </button>
                    )}
                </div>
                {error && <div className="form-error" role="alert">{error}</div>}
                {formOpen && (
                    <form
                        className="merchant-external-form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            void run(() =>
                                linkMerchantExternalPair(token, merchant.id, {
                                    source: source.trim(),
                                    accountCode: accountCode.trim(),
                                }),
                            );
                        }}
                    >
                        <label className="form-field">
                            <span>Источник</span>
                            <input
                                value={source}
                                onChange={(event) => setSource(event.target.value)}
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span>Кабинет</span>
                            <input
                                value={accountCode}
                                onChange={(event) => setAccountCode(event.target.value)}
                                placeholder="alpha"
                                required
                            />
                        </label>
                        <div className="section-header-actions">
                            <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
                                Связать
                            </button>
                            <button
                                className="btn btn-ghost btn-sm"
                                type="button"
                                onClick={() => setFormOpen(false)}
                                disabled={busy}
                            >
                                Отмена
                            </button>
                        </div>
                    </form>
                )}
            </section>
        );
    }

    return (
        <section className="merchant-rate-block merchant-external-block">
            <div className="section-header">
                <div>
                    <h3>Кабинет площадки</h3>
                    <p className="section-hint">
                        {link.autoProvisioned
                            ? "Заведён автоматически при первой заявке этого кабинета."
                            : "Кабинет привязан вручную."}{" "}
                        Один кабинет — один наш мерчант: баланс и расчёт на площадке принадлежат
                        кабинету, а не клиенту, который через него пришёл.
                    </p>
                </div>
                <div className="section-header-actions">
                    <button
                        className={`btn btn-sm ${link.ordersEnabled ? "btn-danger" : "btn-primary"}`}
                        type="button"
                        disabled={busy}
                        onClick={() =>
                            void run(() =>
                                setMerchantExternalOrders(token, merchant.id, !link.ordersEnabled),
                            )
                        }
                    >
                        {link.ordersEnabled ? "Не принимать заявки" : "Принимать заявки"}
                    </button>
                    {/* Отвязки здесь больше нет. Она была придумана, когда
                        автозаведённый мерчант должен был уступить место клиенту,
                        которого мы уже знали. С правилом «один кабинет — один
                        мерчант» этот сценарий исчез, а сама операция стала
                        опасной: баланс мерчанта читается из книги кабинета, и
                        после отвязки следующая заявка завела бы второго мерчанта
                        с собственной проводкой открытия — деньги одного кабинета
                        посчитались бы дважды, и снять это было бы нечем. */}
                </div>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="trader-meta-grid">
                <div className="meta-card">
                    <span className="meta-label">Источник</span>
                    <span className="meta-value mono">{link.source}</span>
                </div>
                <div className="meta-card">
                    <span className="meta-label">Кабинет</span>
                    <span className="meta-value mono">{link.accountCode}</span>
                </div>
                <div className="meta-card">
                    <span className="meta-label">Принимать заявки</span>
                    <span className={`meta-value merchant-vip-value ${link.ordersEnabled ? "active" : ""}`}>
                        {link.ordersEnabled ? "Включено" : "Выключено"}
                    </span>
                </div>
                <div className="meta-card">
                    <span className="meta-label">Расчёт гарантирован источником</span>
                    <span className="meta-value">
                        {merchant.settlementGuaranteed ? "Да" : "Нет"}
                    </span>
                </div>
            </div>
            {link.source === "paylord" && <PaylordPoolSettings token={token} accountCode={link.accountCode}/>}
        </section>
    );
}
